import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authHistoryService, type AuthHistoryParams, type AuthHistoryResponse } from '../services/authHistoryService';

// Ключи для кеша
export const authHistoryKeys = {
  all: ['authHistory'] as const,
  lists: () => [...authHistoryKeys.all, 'list'] as const,
  list: (params: AuthHistoryParams) => [...authHistoryKeys.lists(), params] as const,
  my: (params: AuthHistoryParams) => [...authHistoryKeys.all, 'my', params] as const,
};

// Placeholder данные для мгновенного отображения UI
const placeholderAuthHistory: AuthHistoryResponse = {
  data: [],
  current_page: 1,
  per_page: 25,
  total: 0,
  last_page: 1,
};

/**
 * Хук для получения истории авторизаций (для админа)
 */
export const useAuthHistory = (params: AuthHistoryParams = {}, enabled: boolean = true) => {
  const queryClient = useQueryClient();
  
  const cacheKey = authHistoryKeys.list(params);
  
  // Получаем данные из кеша синхронно
  const cachedData = queryClient.getQueryData<AuthHistoryResponse>(cacheKey);
  // Проверяем похожие запросы (без поиска) для использования в качестве fallback
  const similarCacheKey = authHistoryKeys.list({ ...params, search: undefined });
  const similarCachedData = queryClient.getQueryData<AuthHistoryResponse>(similarCacheKey);
  
  // Определяем начальные данные для мгновенного отображения
  const initialData = cachedData ?? similarCachedData ?? placeholderAuthHistory;
  
  // Отключаем автоматическое обновление во время поиска
  const hasSearch = Boolean(params.search && params.search.trim());
  
  const query = useQuery({
    queryKey: cacheKey,
    queryFn: () => authHistoryService.getHistory(params),
    enabled, // Обновлять только когда включено
    staleTime: hasSearch ? 0 : 1000 * 5, // При поиске данные сразу устаревают
    gcTime: 1000 * 60 * 5, // 5 минут - время хранения в кеше
    refetchInterval: enabled && !hasSearch ? 1000 * 3 : false, // Автоматическое обновление только если нет поиска
    refetchIntervalInBackground: true, // Обновлять даже когда вкладка неактивна
    refetchOnMount: hasSearch ? true : false, // При поиске всегда обновляем
    // Используем кешированные данные как placeholder для мгновенного отображения
    placeholderData: (previousData) => {
      // Если есть предыдущие данные из запроса, используем их
      if (previousData) return previousData;
      // Иначе используем кеш или похожий кеш
      return cachedData ?? similarCachedData ?? placeholderAuthHistory;
    },
    // Начальные данные для мгновенного отображения
    initialData: initialData,
    initialDataUpdatedAt: (cachedData || similarCachedData) ? Date.now() - 1000 : undefined,
  });
  
  // Всегда возвращаем данные (из кеша, placeholder или реальные)
  // Приоритет: реальные данные > кеш > похожий кеш > placeholder
  const data = query.data ?? cachedData ?? similarCachedData ?? placeholderAuthHistory;
  
  return {
    ...query,
    data,
    // isLoading только если нет данных вообще и идет загрузка
    isLoading: query.isLoading && !cachedData && !similarCachedData && !query.data,
  };
};

/**
 * Хук для получения истории авторизаций текущего пользователя
 */
export const useMyAuthHistory = (params: AuthHistoryParams = {}, enabled: boolean = true) => {
  const queryClient = useQueryClient();
  
  const cacheKey = authHistoryKeys.my(params);
  
  // Получаем данные из кеша синхронно
  const cachedData = queryClient.getQueryData<AuthHistoryResponse>(cacheKey);
  // Проверяем похожие запросы (с другими параметрами пагинации) для использования в качестве fallback
  const similarCacheKey = authHistoryKeys.my({ ...params, page: 1 });
  const similarCachedData = queryClient.getQueryData<AuthHistoryResponse>(similarCacheKey);
  
  // Определяем начальные данные для мгновенного отображения
  const initialData = cachedData ?? similarCachedData ?? placeholderAuthHistory;
  
  const query = useQuery({
    queryKey: cacheKey,
    queryFn: () => authHistoryService.getMyHistory(params),
    enabled, // Обновлять только когда включено
    staleTime: 1000 * 5, // 5 секунд - данные считаются свежими
    gcTime: 1000 * 60 * 5, // 5 минут - время хранения в кеше
    refetchInterval: enabled ? 1000 * 3 : false, // Автоматическое обновление каждые 3 секунды только если включено
    refetchIntervalInBackground: true, // Обновлять даже когда вкладка неактивна
    // Используем кешированные данные как placeholder для мгновенного отображения
    placeholderData: (previousData) => {
      // Если есть предыдущие данные из запроса, используем их
      if (previousData) return previousData;
      // Иначе используем кеш или похожий кеш
      return cachedData ?? similarCachedData ?? placeholderAuthHistory;
    },
    // Начальные данные для мгновенного отображения
    initialData: initialData,
    initialDataUpdatedAt: (cachedData || similarCachedData) ? Date.now() - 1000 : undefined,
    // Не обновлять при монтировании, если есть свежие данные в кеше
    refetchOnMount: false,
  });
  
  // Всегда возвращаем данные (из кеша, placeholder или реальные)
  // Приоритет: реальные данные > кеш > похожий кеш > placeholder
  const data = query.data ?? cachedData ?? similarCachedData ?? placeholderAuthHistory;
  
  return {
    ...query,
    data,
    // isLoading только если нет данных вообще и идет загрузка
    isLoading: query.isLoading && !cachedData && !similarCachedData && !query.data,
  };
};

/**
 * Хук для завершения сессии
 */
export const useTerminateSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => authHistoryService.terminateSession(id),
    onSuccess: () => {
      // Инвалидируем все запросы истории авторизаций
      queryClient.invalidateQueries({ queryKey: authHistoryKeys.all });
    },
  });
};
